import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useRequest } from '@frontend/api';

import { MFG_ITEMS_SUBQUERY, MFG_QUERY_KEYS } from './constants';
import type {
  AdskMfgQueryResource,
  FusionItem,
  AdskMfgParams,
  AdskMfgQueryResponse,
  AdskMfgInfiniteQueryFunction,
} from './types';
import { adskRequest, isAdskMfgError } from './utils';

const GRAPHQL_QUERY = {
  operationName: 'GetItemsByFolder',
  query: `query GetItemsByFolder($hubId: ID!, $folderId: ID!, $pagination: PaginationInput) {
        itemsByFolder(hubId: $hubId, folderId: $folderId, pagination: $pagination) {
            pagination {
                cursor
            }
            results ${MFG_ITEMS_SUBQUERY}
        }
    }`,
};

type DataType = {
  itemsByFolder: AdskMfgQueryResource<FusionItem[]>;
};

type Params = AdskMfgParams & {
  hubId: string | null;
  folderId: string | null;
};

type ListItemsByFolderQueryKey = readonly [
  typeof MFG_QUERY_KEYS.LIST_ITEMS_BY_FOLDER,
  string,
  string,
];

export const getListItemsByFolderQueryKey = (
  hubId: string,
  folderId: string,
): ListItemsByFolderQueryKey => [
  MFG_QUERY_KEYS.LIST_ITEMS_BY_FOLDER,
  hubId,
  folderId,
];

export const useListItemsByFolder: AdskMfgInfiniteQueryFunction<
  AdskMfgQueryResponse<DataType>,
  [Params],
  ListItemsByFolderQueryKey,
  FusionItem
> = (
  {
    accessToken,
    refreshAccess,
    hubId,
    folderId,
    pagination = { cursor: null },
  },
  { queryOptions } = {},
) => {
  const queryClient = useQueryClient();
  const request = useRequest();
  const results = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- adskRequest should be built with a hook, pagination should be added to the query key
    queryKey: getListItemsByFolderQueryKey(hubId ?? '', folderId ?? ''),
    queryFn: ({ signal, pageParam }) =>
      adskRequest<DataType>(
        accessToken ?? '',
        {
          ...GRAPHQL_QUERY,
          variables: {
            hubId,
            folderId,
            pagination: { ...pagination, cursor: pageParam },
          },
        },
        request,
        { init: { signal } },
        (data) => !!data.itemsByFolder,
        (msg) =>
          `Failed to list items for folder ${folderId} in hub ${hubId}: ${msg}`,
      ),
    enabled: !!accessToken && !!hubId && !!folderId,
    getNextPageParam: (currentPage) =>
      currentPage.data.itemsByFolder.pagination.cursor === null
        ? null
        : currentPage.data.itemsByFolder.pagination.cursor,
    ...queryOptions,
    onError: async (error) => {
      // If not an ADSK error, bail
      if (!isAdskMfgError(error)) return;

      // Trigger refresh on 401s
      if (error.statusCode === 401) {
        await refreshAccess();

        // Invalidate query to trigger a new request
        queryClient.invalidateQueries({
          queryKey: getListItemsByFolderQueryKey(hubId ?? '', folderId ?? ''),
        });
        return;
      }

      // Run the passed in onError if provided
      queryOptions?.onError?.(error);
    },
  });

  const flatData = useMemo(
    () =>
      results?.data?.pages?.flatMap((page) => page.data.itemsByFolder.results),
    [results?.data?.pages],
  );

  const flatErrors = useMemo(
    () => results?.data?.pages?.flatMap((page) => page?.errors ?? []),
    [results?.data?.pages],
  );

  return { ...results, flatData, flatErrors };
};
